import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { DefaultButton } from '@bingads-webui-react/fabric-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { ValidatedInput as Input } from '@bingads-webui-campaign-react/fluent-with-validation';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { CouponOfferContent } from './components/coupon-offer-content';
import { getPromotionalOfferViewStyles } from './styles/promotional-offer-view-styles';
import { useCouponProcessor } from './hooks/use-coupon-processor';
import { PromotionalOfferViewShimmer } from './components/promotional-offer-view-shimmer';
import { CouponDetailsProps } from './coupon-props';

export const PromotionalOfferView = (props) => {
  const {
    resolvedCoupon,
    onCouponChanged,
    getCouponCurrencyCode,
    channelOfferText,
    showCouponCode,
  } = props;

  const {
    couponValue,
    couponDetails,
    isLoading: isCouponLoading,
  } = resolvedCoupon || {};
  const { couponString } = couponValue || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getPromotionalOfferViewStyles);

  const [{
    couponDisplayCode,
    state,
    isCouponValidAndResolved,
  }, {
    isUserActionDisabled,
    onCouponAction,
    onCouponCodeChange,
    onValidate,
  }] = useCouponProcessor({ coupon: resolvedCoupon });

  const couponActions = useMemo(() => ({
    add: { id: 'add', text: _TL_('Add') },
    remove: { id: 'remove', text: _TL_('Remove') },
  }), []);

  const handleCouponAction = useCallback((type) => {
    onCouponAction(type);
    if (type === couponActions.add.id) {
      onCouponChanged(state.newCoupon);
    } else {
      onCouponChanged('');
    }
  }, [couponActions.add.id, onCouponAction, onCouponChanged, state.newCoupon]);

  const schema = useMemo(() => ({
    name: 'Promotional Code Schema',
    type: 'object',
    properties: {
      PromotionalCode: {
        type: 'string',
        required: false,
        maxLength: 64,
      },
    },
  }), []);

  const serverError = useMemo(
    () => (state.serverError ? [state.serverError] : []),
    [state.serverError]
  );

  const renderCouponActionButton = useCallback(type => (
    <DefaultButton
      className={classNames('coupon-action-button', classes.actionButton)}
      onClick={() => handleCouponAction(type)}
      name="couponEditButton"
      disabled={isUserActionDisabled(type)}
    >
      {i18n.getString(couponActions[type].text)}
    </DefaultButton>
  ), [classes.actionButton, isUserActionDisabled, i18n, couponActions, handleCouponAction]);

  const renderCouponInput = useCallback(() => (
    <div className={classNames('promotional-code-input-container', classes.promotionalCodeInputContainer)}>
      <ValidationGroup validationProps={{ schema, i18n: legacyI18n, onValidate }} >
        <div className={classNames('promotional-code-label', classes.promotionalCodeLabel)} id="promotionalCodeLabel">
          {i18n.getString(_TL_('Promotional offer (optional)'))}
        </div>
        <Input
          id="promotional-code-input"
          aria-labelledby="promotionalCodeLabel"
          data-clarity-unmask="true"
          styles={classes.promotionalCodeInput}
          name="promotionalCodeInput"
          value={couponDisplayCode}
          validationProps={{
            field: 'PromotionalCode',
            passiveMessages: serverError,
          }}
          onChange={onCouponCodeChange}
          placeholder={i18n.getString(_TL_('Enter a valid promotional offer code'))}
        />
      </ValidationGroup>
      {renderCouponActionButton(state.currentAction)}
    </div >
  ), [couponDisplayCode,
    state.currentAction,
    serverError,
    schema,
    renderCouponActionButton,
    onValidate,
    onCouponCodeChange,
    legacyI18n,
    i18n,
    classes]);

  return (
    <div className={classNames('promotional-offer-view', classes.promotionalOfferContainer)}>
      {isCouponValidAndResolved &&
        <div className={classNames('promotion-offer-valid', classes.promotionalOfferContent)}>
          <CouponOfferContent
            couponDetails={couponDetails}
            showCouponCode={showCouponCode}
            couponCodeString={couponValue ? couponString : null}
            showCouponIcon={false}
            channelOfferText={channelOfferText}
            getCouponCurrencyCode={getCouponCurrencyCode}
          />
          {renderCouponActionButton(state.currentAction)}
        </div>
      }
      {(!isCouponLoading && !state.isResolving && !isCouponValidAndResolved) &&
        renderCouponInput()
      }
      {
        (isCouponLoading || state.isResolving) &&
        <div className={classNames('promotion-offer-shimmer', classes.promotionalOfferShimmer)}>
          <PromotionalOfferViewShimmer />
        </div>
      }
    </div>
  );
};

PromotionalOfferView.propTypes = {
  onCouponChanged: PropTypes.func.isRequired,
  resolvedCoupon: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    websiteValidationResult: PropTypes.string,
    couponDetails: CouponDetailsProps,
    couponValue: PropTypes.shape({
      couponString: PropTypes.string,
      couponStringType: PropTypes.string,
    }),
    isManualCouponCode: PropTypes.bool,
    isCouponValid: PropTypes.bool,
    isAcquisitionCoupon: PropTypes.bool,
    isCouponExpired: PropTypes.bool,
  }),
  getCouponCurrencyCode: PropTypes.func,
  channelOfferText: PropTypes.string,
  showCouponCode: PropTypes.bool,
};

PromotionalOfferView.defaultProps = {
  resolvedCoupon: {
    couponValue: { couponString: null, couponStringType: null },
    couponDetails: null,
    isAcquisitionCoupon: null,
    isManualCouponCode: null,
    isCouponValid: null,
    websiteValidationResult: null,
    isCouponExpired: null,
  },
  getCouponCurrencyCode: _.noop,
  channelOfferText: undefined,
  showCouponCode: false,
};
